import { useState } from "react";
import { ArrowDown, CheckCircle2, HelpCircle, XCircle } from "lucide-react";
import { API, fmtUSD } from "@/lib/api";
import { Indicators, IsoUpload } from "./AmlIngest";
import { DecisionBox, ItemList, Pill } from "./AmlShared";

function Hop({ h }) {
  const unknown = h.unknown || h.name?.startsWith("Unknown");
  return (
    <div data-testid={`chain-hop-${h.role.split(" ")[0].toLowerCase()}`} className={`rounded-xl p-3 ring-1 ${unknown ? "bg-amber-50 ring-amber-300 border-dashed" : h.shell ? "bg-red-50 ring-red-200" : "bg-white ring-slate-200"}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{h.role}</div>
      <div className="text-sm font-semibold text-slate-900">{h.name}</div>
      {h.kind === "institution" && !unknown && (
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] num text-slate-500 mt-1">
          <span>BIC {h.bic}</span><span>LEI {h.lei || "not found"}</span><span>{h.country}</span>
          <span className={h.licensed ? "text-emerald-700" : "text-amber-700"}>{h.licensed ? `Licensed · ${h.regulator}` : "Licence not verified"}</span>
          {h.shell && <span className="text-red-700 font-semibold">Shell-bank indicator</span>}
        </div>
      )}
      {h.kind === "institution" && h.match && <div className="text-[10px] text-slate-500 mt-1" data-testid={`hop-screen-${h.hop_order}`}><span className={h.match === "Confirmed match" ? "text-emerald-700" : "text-amber-700"}>{h.match} ({Math.round(h.confidence * 100)}%)</span> · {h.match_basis} · {h.source}, as of {h.as_of}{h.restricted && <span className="text-red-700"> · {h.restricted}</span>} · field {h.evidence}</div>}
      {unknown && <div className="text-[11px] text-amber-800 mt-1">Not present in the message. OmniFi does not infer it.</div>}
    </div>
  );
}

const QI = { ok: [CheckCircle2, "text-emerald-600"], fail: [XCircle, "text-red-600"] };

export function NestedPayments({ data, onData, focus }) {
  const [sel, setSel] = useState(data.items.some((i) => i.id === focus) ? focus : data.items[0].id);
  const p = data.items.find((i) => i.id === sel);
  return (
    <div className="space-y-4"><IsoUpload onData={onData} />
    <div className="grid xl:grid-cols-[300px_1fr] gap-5">
      <ItemList items={data.items} sel={sel} setSel={setSel} render={(i) => (<>
        <div className="flex justify-between gap-2"><span className="num text-xs font-semibold text-slate-900">{i.id}</span><Pill d={i.analyst?.decision || i.engine_decision} id={`aml-list-status-${i.id}`} /></div>
        <div className="text-[11px] text-slate-500 mt-1">{i.message_type} · {i.corridor}{i.source === "Uploaded message" ? " · uploaded" : ""}</div>
        <div className="text-[11px] num text-slate-700">{i.currency} {fmtUSD(i.amount).replace("$", "")}</div>
      </>)} />
      <div className="space-y-4 min-w-0" data-testid="nested-payment-detail">
        <div className="text-xs text-slate-600">{p.note}</div>
        <div className="grid 2xl:grid-cols-2 gap-4">
          <div className="panel p-4">
            <div className="eyebrow mb-3">Reconstructed payment chain</div>
            <div className="space-y-1.5">{p.chain.map((h, n) => <div key={n}>{n > 0 && <ArrowDown className="w-3.5 h-3.5 text-slate-300 mx-auto mb-1.5" />}<Hop h={h} /></div>)}</div>
          </div>
          <div className="space-y-4">
            <div className="panel p-4" data-testid="decision-questions">
              <div className="eyebrow mb-2">Questions answered before release</div>
              {p.questions.map((q) => { const [I, c] = QI[q.status] || [HelpCircle, "text-slate-400"]; return (
                <div key={q.q} className="flex gap-2 py-1.5 border-t border-slate-100 first:border-0"><I className={`w-4 h-4 shrink-0 ${c}`} /><div><div className="text-xs font-medium text-slate-900">{q.q}</div><div className="text-[11px] text-slate-600">{q.answer}</div></div></div>); })}
            </div>
            <div className="panel p-4" data-testid="engine-reasons">
              <div className="flex justify-between items-center mb-2"><span className="eyebrow">Decision engine</span><Pill d={p.engine_decision} id="engine-decision" /></div>
              {p.reasons.map((r) => <div key={r} className="text-[11px] text-slate-700 py-1 border-t border-slate-100">{r}</div>)}
            </div>
          </div>
        </div>
        <Indicators p={p} />
        <div className="panel overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-100 eyebrow flex justify-between">Message field lineage · {p.message_type}{p.source === "Uploaded message" && <a data-testid="raw-message-link" href={`${API}/aml/payments/${p.id}/raw`} target="_blank" rel="noreferrer" className="text-sky-700 normal-case">raw payload</a>}</div>
          <table className="w-full text-xs"><tbody>{p.fields.map(([f, v]) => <tr key={f} className="border-t border-slate-100"><td className="px-4 py-1.5 num text-slate-500 w-1/2">{f}</td><td className={`px-4 py-1.5 ${v.includes("absent") ? "text-amber-700 font-semibold" : "text-slate-800"}`}>{v}</td></tr>)}</tbody></table>
        </div>
        <DecisionBox key={p.id} kind="payments" item={p} choices={data.choices} onData={onData} />
      </div>
    </div></div>
  );
}
